import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment.development';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';
import {
  ApiResponse,
  ApiResponseCustomQuestionPaginated,
  CustomQuestionModel,
  customQuestionQueryParams,
  QuestionItem,
  QuestionSequenceNumbers,
} from '../manage-settings.interface';
import { ApiRespones } from '../../manage-organization/settings/settings.interface';
import { ApiResponses } from '../../../core/models/common.models';

@Injectable({
  providedIn: 'root',
})
export class CustomQuestionService {
  private readonly BASE_URL = environment.apiUrl;
  private readonly API_URL = `${this.BASE_URL}/OrganizationCustomQuestion`;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);

  private readonly customQuestionSubject =
    new BehaviorSubject<ApiResponseCustomQuestionPaginated | null>(null);
  readonly customQuestions$ = this.customQuestionSubject.asObservable();
  private readonly orgCustomQuestions =
    new BehaviorSubject<ApiRespones<QuestionItem> | null>(null);
  readonly OrgcustomQuestions$ = this.orgCustomQuestions.asObservable();

  getCustomQuestions(
    queryParams: customQuestionQueryParams
  ): Observable<ApiResponseCustomQuestionPaginated> {
    return this.http
      .get<ApiResponseCustomQuestionPaginated>(
        `${this.API_URL}/get-org-custom-question-list`,
        {
          params: this.buildQueryParams(queryParams),
        }
      )
      .pipe(
        tap(response => this.customQuestionSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  private buildQueryParams({
    pageIndex,
    pageSize,
    sortOrder,
    sort,
    customQuestion,
    parentOrganizationId,
  }: customQuestionQueryParams): HttpParams {
    let params = new HttpParams()
      .set('Page', pageIndex.toString())
      .set('PageSize', pageSize.toString());

    if (sort) {
      params = params.set('sort', sort);
    }

    if (sortOrder !== undefined) {
      params = params.set('sortOrder', sortOrder.toString());
    }
    if (customQuestion) {
      params = params.set('customQuestion', customQuestion);
    }
    if (parentOrganizationId) {
      params = params.set(
        'parentOrganizationId',
        parentOrganizationId.toString()
      );
    }

    return params;
  }

  getQuestionsByOrg(id: number): Observable<ApiResponses<QuestionItem>> {
    return this.http
      .get<
        ApiResponses<QuestionItem>
      >(`${this.API_URL}/get-question-by-org-id?organizationId=${id}`)
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getQuestionsByTemplateId(id: number): Observable<ApiResponses<QuestionItem>> {
    return this.http
      .get<
        ApiResponses<QuestionItem>
      >(`${this.API_URL}/get-custom-question-by-template-id?bipTemplateId=${id}`)
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
  createQuestion(
    payload: CustomQuestionModel,
    id: number
  ): Observable<ApiResponse<void>> {
    return this.http
      .post<
        ApiResponse<void>
      >(`${this.API_URL}/create-org-custom-question?organizationId=${id}`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  updateQuestion(
    payload: CustomQuestionModel,
    id: number
  ): Observable<ApiResponse<void>> {
    return this.http
      .put<
        ApiResponse<void>
      >(`${this.API_URL}/update?organizationId=${id}`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  updateSequenceNumber(payload: QuestionSequenceNumbers) {
    return this.http
      .put<ApiResponse<void>>(`${this.API_URL}/update-sequence-number`, payload)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
}
